import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Shield, 
  Settings,
  Users,
  Crown,
  Star,
  UserMinus,
  TrendingUp,
  Coins,
  Upload,
  Megaphone,
  Send,
  Edit,
  AlertCircle,
  UserPlus,
  Check,
  X,
  Clock
} from "lucide-react";

const LEVEL_NAMES = {
  1: "Bronz",
  2: "Gümüş",
  3: "Altın",
  4: "Platin",
  5: "Elmas"
};

const LEVEL_COLORS = {
  1: "bg-amber-700",
  2: "bg-gray-400",
  3: "bg-yellow-500",
  4: "bg-cyan-400",
  5: "bg-purple-500",
};

const LEVEL_UPGRADE_COSTS = {
  2: 50,
  3: 150,
  4: 300,
  5: 500,
};

const ROLE_NAMES = {
  president: "Başkan",
  manager: "Yönetici",
  captain: "Kaptan",
  player: "Oyuncu"
};

const ClubManagePage = ({ user }) => {
  const navigate = useNavigate();
  const { clubId } = useParams();
  const fileInputRef = useRef(null);
  
  const [club, setClub] = useState(null);
  const [balance, setBalance] = useState(0);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [reviewingApp, setReviewingApp] = useState(null);
  
  const [editData, setEditData] = useState({});
  const [announcement, setAnnouncement] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [showKickDialog, setShowKickDialog] = useState(false);
  const [showPromoteDialog, setShowPromoteDialog] = useState(false);
  const [newRole, setNewRole] = useState("player");

  useEffect(() => {
    fetchData();
  }, [clubId]);

  const fetchData = async () => {
    try {
      const [clubRes, balanceRes, applicationsRes] = await Promise.all([
        axios.get(`${API}/clubs/${clubId}`),
        axios.get(`${API}/credits/balance`),
        axios.get(`${API}/clubs/${clubId}/applications`).catch(() => ({ data: [] }))
      ]);
      
      setClub(clubRes.data);
      setBalance(balanceRes.data.balance);
      setApplications(applicationsRes.data || []);
      setEditData({
        name: clubRes.data.name,
        description: clubRes.data.description || "",
        isRecruiting: clubRes.data.isRecruiting,
        city: clubRes.data.city || "",
        district: clubRes.data.district || "",
        primaryColor: clubRes.data.primaryColor || "#10b981",
        secondaryColor: clubRes.data.secondaryColor || "#ffffff"
      });
    } catch (error) {
      toast.error("Kulüp bulunamadı");
      navigate("/clubs");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/clubs/${clubId}`, editData);
      toast.success("Değişiklikler kaydedildi");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setSaving(false);
    }
  };

  const handleUpgradeLevel = async () => {
    if (!club || club.level >= 5) return;
    
    const cost = LEVEL_UPGRADE_COSTS[club.level + 1];
    if (balance < cost) {
      toast.error(`Seviye yükseltmek için ${cost} kredi gerekli`);
      return;
    }
    
    setUpgrading(true);
    try {
      const response = await axios.post(`${API}/clubs/${clubId}/upgrade-level`);
      toast.success(response.data.message);
      fetchData();
      // Refresh balance
      const balanceRes = await axios.get(`${API}/credits/balance`);
      setBalance(balanceRes.data.balance);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Seviye yükseltilemedi");
    } finally {
      setUpgrading(false);
    }
  };

  const handleKickMember = async () => {
    if (!selectedMember) return;
    
    try {
      await axios.post(`${API}/clubs/${clubId}/kick/${selectedMember.userId}`);
      toast.success("Üye kulüpten çıkarıldı");
      setShowKickDialog(false);
      setSelectedMember(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  const handlePromoteMember = async () => {
    if (!selectedMember) return;
    
    try {
      await axios.post(`${API}/clubs/${clubId}/promote/${selectedMember.userId}?role=${newRole}`);
      toast.success(`Üye ${ROLE_NAMES[newRole]} olarak atandı`);
      setShowPromoteDialog(false);
      setSelectedMember(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  const handleSendAnnouncement = async () => {
    if (!announcement.trim()) {
      toast.error("Duyuru içeriği gerekli");
      return;
    }
    
    try {
      await axios.post(`${API}/clubs/${clubId}/announcements?content=${encodeURIComponent(announcement)}`);
      toast.success("Duyuru paylaşıldı");
      setAnnouncement("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Duyuru gönderilemedi");
    }
  };

  const handleReviewApplication = async (applicationId, status) => {
    setReviewingApp(applicationId);
    try {
      const response = await axios.post(`${API}/clubs/${clubId}/applications/${applicationId}/review`, {
        status: status
      });
      if (response.data.success) {
        toast.success(response.data.message);
        fetchData(); // Refresh both club and applications
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setReviewingApp(null);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Dosya boyutu 2MB'dan küçük olmalı");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await axios.put(`${API}/clubs/${clubId}`, {
          logo: reader.result,
          logoType: "custom"
        });
        toast.success("Logo güncellendi");
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Logo yüklenemedi");
      }
    };
    reader.readAsDataURL(file);
  };

  const myRole = club?.members?.find(m => m.userId === user?.id)?.role;
  const isPresident = myRole === "president";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!club) return null;

  const nextLevelCost = LEVEL_UPGRADE_COSTS[club.level + 1];
  const currentManagers = club.members?.filter(m => m.role === "manager").length || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="container mx-auto px-4 py-8">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/20 mb-4"
            onClick={() => navigate(`/clubs/${clubId}`)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Kulübe Dön
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Settings className="h-8 w-8" />
                Kulüp Yönetimi
              </h1>
              <p className="text-emerald-100 mt-1">{club.name}</p>
            </div>
            
            <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2">
              <p className="text-xs text-emerald-100">Bakiye</p>
              <p className="text-xl font-bold flex items-center gap-1">
                <Coins className="h-4 w-4" />
                {balance}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Tabs defaultValue="settings" className="w-full">
          <TabsList className="grid w-full grid-cols-5 mb-6">
            <TabsTrigger value="settings">
              <Settings className="h-4 w-4 mr-2" />
              Ayarlar
            </TabsTrigger>
            <TabsTrigger value="applications" className="relative">
              <UserPlus className="h-4 w-4 mr-2" />
              Başvurular
              {applications.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {applications.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="members">
              <Users className="h-4 w-4 mr-2" />
              Üyeler
            </TabsTrigger>
            <TabsTrigger value="level">
              <TrendingUp className="h-4 w-4 mr-2" />
              Seviye
            </TabsTrigger>
            <TabsTrigger value="announce">
              <Megaphone className="h-4 w-4 mr-2" />
              Duyuru
            </TabsTrigger>
          </TabsList>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Kulüp Ayarları</CardTitle>
                <CardDescription>Temel kulüp bilgilerini düzenleyin</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Kulüp Adı</Label>
                  <div className="flex gap-2">
                    <Input
                      value={editData.name}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      disabled={!isPresident}
                    />
                    {isPresident && (
                      <Badge variant="outline" className="whitespace-nowrap">
                        30 Kredi (30 gün)
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Açıklama</Label>
                  <Textarea
                    value={editData.description}
                    onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                    rows={3}
                  />
                </div>

                {/* Renk Seçiciler */}
                <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                  <p className="font-medium text-gray-700">Kulüp Renkleri</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Ana Renk</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={editData.primaryColor}
                          onChange={(e) => setEditData({ ...editData, primaryColor: e.target.value })}
                          className="w-12 h-10 rounded cursor-pointer border"
                        />
                        <Input
                          value={editData.primaryColor}
                          onChange={(e) => setEditData({ ...editData, primaryColor: e.target.value })}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>İkinci Renk</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={editData.secondaryColor}
                          onChange={(e) => setEditData({ ...editData, secondaryColor: e.target.value })}
                          className="w-12 h-10 rounded cursor-pointer border"
                        />
                        <Input
                          value={editData.secondaryColor}
                          onChange={(e) => setEditData({ ...editData, secondaryColor: e.target.value })}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                  {/* Renk Önizleme */}
                  <div className="flex items-center gap-4 mt-3">
                    <span className="text-sm text-gray-500">Önizleme:</span>
                    <div 
                      className="w-12 h-12 rounded-full border-4"
                      style={{ 
                        backgroundColor: editData.primaryColor,
                        borderColor: editData.secondaryColor 
                      }}
                    />
                    <div 
                      className="h-8 flex-1 rounded"
                      style={{ 
                        background: `linear-gradient(to right, ${editData.primaryColor}, ${editData.secondaryColor})`
                      }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>İl</Label>
                    <Input
                      value={editData.city}
                      onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>İlçe</Label>
                    <Input
                      value={editData.district}
                      onChange={(e) => setEditData({ ...editData, district: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">Yeni Üye Alımı</p>
                    <p className="text-sm text-gray-500">Kulübe katılım başvuruları</p>
                  </div>
                  <Switch
                    checked={editData.isRecruiting}
                    onCheckedChange={(checked) => setEditData({ ...editData, isRecruiting: checked })}
                  />
                </div>

                {isPresident && (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-amber-800">Özel Logo Yükle</p>
                        <p className="text-sm text-amber-600">100 Kredi (ilk yükleme)</p>
                      </div>
                      <Button 
                        variant="outline"
                        className="border-amber-300"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Yükle
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </div>
                  </div>
                )}

                <Button 
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <UserPlus className="h-5 w-5" />
                    Üyelik Başvuruları
                  </span>
                  {applications.length > 0 && (
                    <Badge variant="destructive">{applications.length} bekleyen</Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Kulübe katılmak isteyen oyuncuların başvurularını yönetin
                </CardDescription>
              </CardHeader>
              <CardContent>
                {applications.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Bekleyen başvuru yok</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {applications.map((app) => (
                      <div 
                        key={app.id}
                        className="p-4 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center overflow-hidden">
                              {app.userImage ? (
                                <img src={app.userImage} alt={app.userName} className="w-full h-full object-cover" />
                              ) : (
                                <Users className="h-6 w-6 text-emerald-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-semibold">{app.userName}</p>
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                {app.userPosition && (
                                  <Badge variant="outline" className="text-xs">
                                    {app.userPosition === "forward" && "Forvet"}
                                    {app.userPosition === "midfielder" && "Orta Saha"}
                                    {app.userPosition === "defender" && "Defans"}
                                    {app.userPosition === "goalkeeper" && "Kaleci"}
                                  </Badge>
                                )}
                                <span>Kondisyon: {app.userKondisyon}</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500">
                              {new Date(app.createdAt).toLocaleDateString('tr-TR')}
                            </p>
                          </div>
                        </div>
                        
                        {app.message && (
                          <div className="mt-3 p-3 bg-white rounded border text-sm text-gray-600">
                            <p className="font-medium text-xs text-gray-400 mb-1">Başvuru Mesajı:</p>
                            {app.message}
                          </div>
                        )}
                        
                        <div className="flex gap-2 mt-4">
                          <Button 
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleReviewApplication(app.id, "accepted")}
                            disabled={reviewingApp === app.id || club.members?.length >= club.memberLimit}
                          >
                            {reviewingApp === app.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                            ) : (
                              <>
                                <Check className="h-4 w-4 mr-1" />
                                Kabul Et
                              </>
                            )}
                          </Button>
                          <Button 
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => handleReviewApplication(app.id, "rejected")}
                            disabled={reviewingApp === app.id}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Reddet
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {club.members?.length >= club.memberLimit && applications.length > 0 && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-700 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Kulüp kapasitesi dolu. Yeni üye kabul etmek için seviye yükseltin.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Üye Yönetimi</span>
                  <Badge>{club.members?.length}/{club.memberLimit}</Badge>
                </CardTitle>
                <CardDescription>
                  Yönetici Limiti: {currentManagers}/{club.managerLimit}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {club.members?.sort((a, b) => {
                    const order = { president: 0, manager: 1, captain: 2, player: 3 };
                    return order[a.role] - order[b.role];
                  }).map((member) => (
                    <div 
                      key={member.userId}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                          {member.role === "president" ? (
                            <Crown className="h-5 w-5 text-yellow-500" />
                          ) : member.role === "manager" ? (
                            <Star className="h-5 w-5 text-blue-500" />
                          ) : (
                            <Users className="h-5 w-5 text-gray-500" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{member.userName}</p>
                          <p className="text-sm text-gray-500">{ROLE_NAMES[member.role]}</p>
                        </div>
                      </div>
                      
                      {member.role !== "president" && (
                        <div className="flex gap-2">
                          {isPresident && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedMember(member);
                                setNewRole(member.role === "player" ? "captain" : "player");
                                setShowPromoteDialog(true);
                              }}
                            >
                              <Edit className="h-3 w-3 mr-1" />
                              Rol
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => {
                              setSelectedMember(member);
                              setShowKickDialog(true);
                            }}
                          >
                            <UserMinus className="h-3 w-3 mr-1" />
                            Çıkar
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Level Tab */}
          <TabsContent value="level">
            <Card>
              <CardHeader>
                <CardTitle>Kulüp Seviyesi</CardTitle>
                <CardDescription>Seviye yükselterek daha fazla özellik açın</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Current Level */}
                <div className="p-6 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-emerald-100 text-sm">Mevcut Seviye</p>
                      <p className="text-3xl font-bold">{LEVEL_NAMES[club.level]}</p>
                    </div>
                    <Badge className={`${LEVEL_COLORS[club.level]} text-white text-lg px-4 py-2`}>
                      Seviye {club.level}
                    </Badge>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-emerald-100">Üye Limiti</p>
                      <p className="font-bold">{club.memberLimit}</p>
                    </div>
                    <div>
                      <p className="text-emerald-100">Yönetici Limiti</p>
                      <p className="font-bold">{club.managerLimit}</p>
                    </div>
                  </div>
                </div>

                {/* Next Level */}
                {club.level < 5 ? (
                  <div className="p-4 border rounded-xl">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="font-medium">Sonraki Seviye: {LEVEL_NAMES[club.level + 1]}</p>
                        <p className="text-sm text-gray-500">
                          +{club.level + 1 === 2 ? 5 : club.level + 1 === 3 ? 10 : club.level + 1 === 4 ? 20 : 50} üye kapasitesi
                          {club.level + 1 >= 3 && `, +1 yönetici`}
                        </p>
                      </div>
                      <Badge variant="outline">{nextLevelCost} Kredi</Badge>
                    </div>
                    
                    {balance < nextLevelCost && (
                      <div className="flex items-center gap-2 text-amber-600 mb-4 text-sm">
                        <AlertCircle className="h-4 w-4" />
                        <span>Yetersiz kredi ({balance}/{nextLevelCost})</span>
                        <Button 
                          size="sm" 
                          variant="link"
                          className="text-amber-600"
                          onClick={() => navigate("/credits")}
                        >
                          Kredi Al
                        </Button>
                      </div>
                    )}
                    
                    <Button
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      onClick={handleUpgradeLevel}
                      disabled={upgrading || balance < nextLevelCost || !isPresident}
                    >
                      {upgrading ? "Yükseltiliyor..." : `${LEVEL_NAMES[club.level + 1]} Seviyesine Yükselt`}
                    </Button>
                    
                    {!isPresident && (
                      <p className="text-center text-sm text-gray-500 mt-2">
                        Sadece başkan seviye yükseltebilir
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="p-6 text-center bg-purple-50 rounded-xl">
                    <Crown className="h-12 w-12 mx-auto text-purple-500 mb-2" />
                    <p className="font-bold text-purple-700">Maksimum Seviye!</p>
                    <p className="text-sm text-purple-600">Kulübünüz en yüksek seviyede</p>
                  </div>
                )}

                {/* Level Benefits */}
                <div className="space-y-2">
                  <p className="font-medium">Seviye Avantajları</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    {Object.entries(LEVEL_NAMES).map(([level, name]) => (
                      <div 
                        key={level}
                        className={`p-3 rounded-lg ${Number(level) <= club.level ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-50 text-gray-400'}`}
                      >
                        <Badge className={`${LEVEL_COLORS[level]} text-white mb-1`}>{name}</Badge>
                        <p className="text-xs">
                          {level === "1" && "12 üye, 1 yönetici"}
                          {level === "2" && "+5 üye"}
                          {level === "3" && "+10 üye, +1 yönetici"}
                          {level === "4" && "+20 üye, +2 yönetici"}
                          {level === "5" && "+50 üye, +3 yönetici"}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Announcement Tab */}
          <TabsContent value="announce">
            <Card>
              <CardHeader>
                <CardTitle>Duyuru Paylaş</CardTitle>
                <CardDescription>Kulüp üyelerine duyuru gönderin</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder="Duyuru içeriğini yazın..."
                  value={announcement}
                  onChange={(e) => setAnnouncement(e.target.value)}
                  rows={4}
                />
                <Button 
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleSendAnnouncement}
                  disabled={!announcement.trim()}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Duyuru Paylaş
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Kick Dialog */}
      <AlertDialog open={showKickDialog} onOpenChange={setShowKickDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Üyeyi Çıkar</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedMember?.userName} kulüpten çıkarılacak. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700"
              onClick={handleKickMember}
            >
              Çıkar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Promote Dialog */}
      <AlertDialog open={showPromoteDialog} onOpenChange={setShowPromoteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rol Değiştir</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedMember?.userName} için yeni rol seçin:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-2 py-4">
            {["manager", "captain", "player"].map((role) => (
              <Button
                key={role}
                variant={newRole === role ? "default" : "outline"}
                onClick={() => setNewRole(role)}
                className={newRole === role ? "bg-emerald-600" : ""}
                disabled={role === "manager" && currentManagers >= club.managerLimit && selectedMember?.role !== "manager"}
              >
                {ROLE_NAMES[role]}
              </Button>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handlePromoteMember}
            >
              Kaydet
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ClubManagePage;
